import dayjs from "dayjs";
import { setStartFormat, setEndFormat } from "../../utils/dateformat/dateFormat";

/**
 * Keywords 페이지가 두 API([GET] templates/keywords/popular, /templates)에
 * 공통으로 쓰는 순수 헬퍼. 스토어/컴포넌트에서 분리해 단위 테스트가 가능하게 했다.
 */

/** 서버가 허용하는 정렬 키. 그 외 값은 400. 첫 항목이 기본값(view). */
export const KEYWORD_SORT_OPTIONS: { label: string; value: KeywordSortBy }[] = [
  { label: "View", value: "view" },
  { label: "Like", value: "like" },
  { label: "Download", value: "download" },
  { label: "Share", value: "share" },
  { label: "AI Score", value: "aiScore" },
];

export type KeywordSortBy = "view" | "like" | "download" | "share" | "aiScore";

export const DEFAULT_SORT_BY: KeywordSortBy = "view";

/**
 * 키워드 기준. tag = 설명의 #해시태그(기본), text = 설명 본문 단어(Atlas Search,
 * 서버 배포 예정 — 배포 전에는 400 이 올 수 있다). 그 외 값은 400.
 */
export type KeywordMatchType = "tag" | "text";

export const MATCH_TYPE_OPTIONS: { label: string; value: KeywordMatchType }[] = [
  { label: "Hashtag", value: "tag" },
  { label: "Description text", value: "text" },
];

export const DEFAULT_MATCH_TYPE: KeywordMatchType = "tag";

export const matchTypeLabel = (value: string) =>
  MATCH_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value;

/** 화면 표기. 해시태그 모드에서만 # 을 붙인다(text 모드 키워드는 본문 단어). */
export const displayKeyword = (keyword: string, matchType: string) => {
  if (matchType === "tag") return `#${keyword}`;
  return keyword;
};

/** 조회 기간 상한(일). 서버는 90일 초과 시 400 을 돌려준다. */
export const MAX_RANGE_DAYS = 90;
/** 기본 조회 기간(일). 서버 기본값(endedAt 기준 30일)과 같게 둔다. */
export const DEFAULT_RANGE_DAYS = 30;

/** limit / templatesPerKeyword / display 의 서버 허용 범위 */
export const LIMIT_RANGE = { min: 1, max: 100, default: 20 };
export const TEMPLATES_PER_KEYWORD_RANGE = { min: 1, max: 50, default: 10 };
export const DISPLAY_RANGE = { min: 1, max: 100, default: 20 };

/**
 * 서버와 같은 규칙으로 키워드를 정규화한다: 소문자, # → 공백, 공백 정리.
 * (#Happy#Diwali → "happy diwali"). text 모드에서는 여러 단어가 모두 포함되는
 * 템플릿을 찾는다. 결과가 빈 문자열이면 서버가 400 을 돌려주므로 호출부에서 막는다.
 */
export const normalizeKeyword = (raw: string): string =>
  raw.toLowerCase().replace(/#/g, " ").replace(/\s+/g, " ").trim();

/**
 * 검색 입력을 해석한다. 앞뒤 공백을 지운 뒤 첫 글자가 # 이면 해시태그(tag) 검색,
 * 아니면 설명 본문(text) 검색이다. 키워드는 서버 규칙(normalizeKeyword)으로 정리해
 * 돌려주고, 정리 후 남는 게 없으면 null.
 *   "#Diwali "      → { keyword: "diwali",       matchType: "tag"  }
 *   "happy diwali"  → { keyword: "happy diwali", matchType: "text" }
 *   "#"  /  "   "   → null
 */
export const parseKeywordSearch = (
  raw: string
): { keyword: string; matchType: KeywordMatchType } | null => {
  const trimmed = raw.trim();
  const keyword = normalizeKeyword(trimmed);
  if (!keyword) return null;
  return { keyword, matchType: trimmed.startsWith("#") ? "tag" : "text" };
};

/**
 * 드릴다운 경로. 해시태그면 # 을 붙여 URL 자체에 유형을 남긴다
 * (`/keywords/%23diwali`). 그래야 뒤로가기·새로고침·링크 공유 때 URL 만으로
 * 유형이 복원된다. 라우트 파라미터는 parseKeywordSearch 로 다시 읽는다.
 */
export const keywordPath = (keyword: string, matchType: KeywordMatchType) =>
  `/keywords/${encodeURIComponent(displayKeyword(keyword, matchType))}`;

/**
 * 기본(비명시) 조회 기간: 오늘을 끝으로 최근 DEFAULT_RANGE_DAYS 일.
 * 호출 시점마다 새로 계산한다 — 스토어나 쿠키에 고정해 두면 날짜가 바뀐 뒤에도
 * "오늘"이 어제로 남아, 종료일이 오늘 23:59:59 까지를 포함하지 못한다.
 */
export const defaultDateRange = () => ({
  startDate: dayjs()
    .subtract(DEFAULT_RANGE_DAYS, "day")
    .startOf("day")
    .toDate(),
  endDate: dayjs().toDate(),
});

/**
 * 날짜 피커의 Date 를 UTC 달력일 경계(00:00:00.000 ~ 23:59:59.999)로 늘려
 * RFC3339 문자열로 만든다. 다른 탭(Templates/Moderation)의 API 레이어와 같은
 * setStartFormat/setEndFormat 를 써 결과가 일치한다.
 */
export const buildKeywordDateRange = (startDate: Date, endDate: Date) => ({
  startedAt: setStartFormat(startDate).toISOString(),
  endedAt: setEndFormat(endDate).toISOString(),
});

/**
 * 서버에 보내기 전에 기간을 검사한다. 위반 시 사용자에게 보여줄 메시지를, 정상이면
 * null 을 돌려준다. 서버 규칙: startedAt < endedAt, 범위 최대 90일.
 * 달력일 단위로 세므로 start 와 end 를 포함해 90일까지 허용한다.
 */
export const validateKeywordRange = (
  startDate: Date,
  endDate: Date
): string | null => {
  const start = dayjs(startDate).startOf("day");
  const end = dayjs(endDate).startOf("day");
  if (end.isBefore(start)) {
    return "The start date must be earlier than the end date.";
  }
  const days = end.diff(start, "day") + 1;
  if (days > MAX_RANGE_DAYS) {
    return `The date range cannot exceed ${MAX_RANGE_DAYS} days (currently ${days} days).`;
  }
  return null;
};

/**
 * Axios 래퍼는 실패해도 throw 하지 않고 에러 객체를 그대로 돌려준다. 정상 응답에만
 * 있는 필드로 판별한다.
 */
export const isPopularResponse = (res: any): res is PopularKeywordsResponse =>
  !!res && Array.isArray(res.keywords);

export const isKeywordTemplatesResponse = (
  res: any
): res is KeywordTemplatesResponse => !!res && Array.isArray(res.templates);

/** 실패 응답에서 사용자에게 보여줄 문구를 뽑는다. */
export const describeKeywordError = (err: any, fallback: string): string => {
  const status = err?.response?.status;
  const message = err?.response?.data?.errorMessage;
  const suffix = [status ? `code: ${status}` : "", message || ""]
    .filter(Boolean)
    .join(", ");
  return suffix ? `${fallback} (${suffix})` : fallback;
};

export interface KeywordTemplateAiScore {
  quality: number;
  editing: number;
  total: number;
}

export interface KeywordTemplate {
  templateId: string;
  videoPath: string;
  description: string;
  likeCounts: number;
  downloadCounts: number;
  shareCounts: number;
  viewCount: number;
  aiScore: KeywordTemplateAiScore | null;
}

export interface PopularKeyword {
  keyword: string;
  templateCount: number;
  templates: KeywordTemplate[];
}

export interface PopularKeywordsResponse {
  country: string;
  /** 적용된 키워드 기준. tag / text */
  matchType: KeywordMatchType;
  startedAt: string;
  endedAt: string;
  scannedTemplates: number;
  keywords: PopularKeyword[];
}

export interface KeywordTemplatesResponse {
  country: string;
  keyword: string;
  matchType: KeywordMatchType;
  startedAt: string;
  endedAt: string;
  templateCount: number;
  page: number;
  display: number;
  templates: KeywordTemplate[];
}
